/**
 * submitSurvey — POST /api/surveys/responses/submit/.
 * The endpoint takes calllogid in the BODY (not URL), so the
 * dependency resolution substitutes into the JSON payload rather than
 * the URL. Depends on the parent visit having synced.
 *
 * applyLocal stores the response bundle in visit_survey_responses
 * keyed by taskUUID (== visitLocalId), so the rep's "you completed
 * this survey" UI shows correct state immediately.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put } from '../../utils/indexedDB';
import {
  BASE_URL,
  jsonHeaders,
  classifyResponse,
  networkErrorResult,
  resolveParentCallLogId,
} from '../httpHelpers';

export interface SurveyResponseEntry {
  questionid: number;
  responseText?: string;
  response_text?: string;
  attachmentRef?: string;
}

export interface SubmitSurveyInput {
  visitLocalId: string;
  surveyId: number;
  responses: SurveyResponseEntry[];
  urgentReview?: boolean;
}

export interface SubmitSurveyResponse {
  success: boolean;
}

export const submitSurveyOp: WriteOp<
  SubmitSurveyInput,
  SubmitSurveyInput,
  SubmitSurveyResponse
> = {
  kind: 'submitSurvey',

  async applyLocal(input) {
    await put(STORES.VISIT_SURVEY_RESPONSES, {
      taskUUID: input.visitLocalId,
      surveyId: input.surveyId,
      responses: input.responses,
      urgentReview: input.urgentReview ?? false,
      submittedAt: new Date().toISOString(),
      syncStatus: 'pending',
    });
  },

  serialize(input, localId) {
    return {
      action: 'submitSurvey',
      endpoint: '/api/surveys/responses/submit/',
      method: 'POST',
      payload: input,
      priority: 30,
      maxRetries: 10,
      dependsOn: input.visitLocalId,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as SubmitSurveyInput;
    const calllogid = await resolveParentCallLogId(input.visitLocalId);
    if (calllogid === undefined) {
      return {
        status: 'pending',
        retriable: true,
        errorMessage: 'Parent visit has not synced yet',
      };
    }
    if (calllogid === null) {
      return {
        status: 'failed',
        retriable: false,
        errorMessage: 'submitSurvey requires a visitLocalId',
      };
    }

    // Server's QuestionResponseSerializer requires `qid` (not questionid) and
    // optional snake_case `response_text` (apps/surveys/apis/serializer.py
    // QuestionResponseSerializer). Sending questionid was rejected with
    // "qid: This field is required" — the dual-write op's #1955/diag-#37 bug.
    const responses = input.responses.map((r) => ({
      qid: r.questionid,
      response_text: r.response_text ?? r.responseText ?? '',
    }));

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${row.endpoint}`, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify({
          surveyid: input.surveyId,
          calllogid,
          responses,
          urgentReview: input.urgentReview ?? false,
        }),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;
    return { status: 'success', retriable: false };
  },

  async reconcile(row) {
    const localId = (row.payload as SubmitSurveyInput).visitLocalId;
    await put(STORES.VISIT_SURVEY_RESPONSES, {
      taskUUID: localId,
      surveyId: (row.payload as SubmitSurveyInput).surveyId,
      responses: (row.payload as SubmitSurveyInput).responses,
      urgentReview: (row.payload as SubmitSurveyInput).urgentReview ?? false,
      submittedAt: row.lastAttempt || new Date().toISOString(),
      syncStatus: 'synced',
    });
  },

  dependsOn(input) {
    return input.visitLocalId;
  },
};

export type SubmitSurveyOp = typeof submitSurveyOp;
